import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowRight, ExternalLink, Globe, Hotel, Info, Loader, Search, Shield } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import { api } from "../utils/apiClient";
import LimitReachedDialog from "./LimitReachedDialog";
import UsageMeter from "./UsageMeter";

const EXAMPLE_RESULTS = [
  {
    provider: "ChatGPT",
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    links: [
      { url: "booking.com/hotel/grand-palace", type: "ota" },
      { url: "grandpalacehotel.com", type: "direct" },
      { url: "expedia.com/hotels/grand-palace", type: "ota" },
      { url: "grandpalacehotel.in", type: "direct" },
    ],
  },
  {
    provider: "Gemini",
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    links: [
      { url: "tripadvisor.com/grand-palace-hotel", type: "ota" },
      { url: "booking.com/hotel/grand-palace", type: "ota" },
      { url: "grandpalacehotel.com/rooms", type: "direct" },
    ],
  },
  {
    provider: "Claude",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    links: [
      { url: "grandpalacehotel.com", type: "direct" },
      { url: "hotels.com/grand-palace", type: "ota" },
      { url: "agoda.com/grand-palace", type: "ota" },
    ],
  },
];

const AnimatedExampleCard = ({ provider, color, bg, border, links }) => (
  <div className={`rounded-2xl border ${border} ${bg} p-4`}>
    <div className={`text-xs font-bold uppercase tracking-widest mb-3 ${color}`}>{provider}</div>
    <div className="space-y-2">
      {links.map((link, i) => (
        <div
          key={i}
          className="flex items-center gap-2 p-2 rounded-lg bg-surface-container-lowest/50"
        >
          <span className="text-on-surface-variant/40 text-xs font-mono w-4 shrink-0">
            {i + 1}.
          </span>
          <span className="text-on-surface text-xs font-mono truncate flex-1">{link.url}</span>
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
              link.type === "direct"
                ? "bg-emerald-500/20 text-emerald-400"
                : "bg-red-500/20 text-red-400"
            }`}
          >
            {link.type === "direct" ? "Direct" : "OTA"}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const OTAEngine = () => {
  const navigate = useNavigate();
  const { user, businessProfile } = useAuth();
  const { byFeature: usage, refresh: refreshUsage } = useUsage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [limitMessage, setLimitMessage] = useState(null);
  // null = user hasn't typed yet, use auto-query; string = user-edited value
  const [customQuery, setCustomQuery] = useState(null);

  const openAuth = (mode) => navigate(`?auth=${mode}`);
  const openBusinessProfile = () => window.dispatchEvent(new CustomEvent("openBusinessProfile"));

  const isProfileComplete = businessProfile?.is_complete;

  const autoQuery =
    businessProfile?.business_name && businessProfile?.city && businessProfile?.country
      ? `Where can I book ${businessProfile.business_name} in ${businessProfile.city}, ${businessProfile.country}?`
      : "";

  const query = customQuery !== null ? customQuery : autoQuery;
  const setQuery = (val) => setCustomQuery(val);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!user) {
      openAuth("login");
      return;
    }

    if (!isProfileComplete) {
      openBusinessProfile();
      return;
    }

    if (!query.trim()) return;

    setError(null);
    setLimitMessage(null);
    setIsSubmitting(true);
    try {
      const res = await api.post("/ota-report/generate", { query: query.trim() });
      navigate(`/ota-direct/report/${res.data.ota_report_id}`, {
        state: { otaResponse: res.data },
      });
    } catch (err) {
      if (err.response?.status === 403) {
        refreshUsage();
        setLimitMessage(err.response?.data?.detail || "You've reached your plan limit.");
        setIsSubmitting(false);
        return;
      }
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((e) => e.msg).join(". ")
            : "Failed to generate report. Please try again.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-10 pb-24 px-6 md:px-12 max-w-8xl mx-auto">
      {limitMessage && !isSubmitting && (
        <LimitReachedDialog message={limitMessage} onClose={() => setLimitMessage(null)} />
      )}
      {/* Hero */}
      <section className="mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-label text-[10px] font-extrabold tracking-[0.15em] mb-6 uppercase">
              OTA vs Direct Booking Tracker
            </span>

            <h1 className="font-headline text-5xl md:text-7xl font-extrabold tracking-tight text-on-surface mb-8 leading-[1.05]">
              Is AI Sending Guests to 3rd party sites instead of You?
            </h1>

            <p className="font-body text-xl text-on-surface-variant max-w-2xl mb-6 leading-relaxed">
              When travellers ask AI to book your hotel, the links it shows matter. We search your
              hotel across ChatGPT, Gemini, and Claude to reveal whether guests are directed to your
              direct website — or handed off to commission-hungry OTAs.
            </p>

            <div className="flex flex-wrap gap-6 mb-12 text-sm text-on-surface-variant">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Direct links = more revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-red-400" />
                <span>OTA links = 15–25% commission loss</span>
              </div>
            </div>

            {/* Query box */}
            <div className="bg-surface-container-low rounded-2xl p-6 border border-outline/10">
              <div className="flex items-start gap-2 mb-4">
                <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-xs text-on-surface-variant leading-relaxed">
                  This query is automatically built from your business profile. It reflects what
                  travellers type when looking to book directly at your property. You can edit it
                  before running.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-outline" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={
                      !user
                        ? "Log in to generate your report…"
                        : !isProfileComplete
                          ? "Complete your business profile to generate…"
                          : businessProfile
                            ? "Enter booking query…"
                            : "Loading your profile…"
                    }
                    disabled={!user || !isProfileComplete || isSubmitting}
                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-surface-container-lowest text-on-surface placeholder:text-outline/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 transition-all"
                  />
                </div>

                {error && <p className="text-error text-sm font-medium">{error}</p>}

                <button
                  type={user ? "submit" : "button"}
                  onClick={!user ? () => openAuth("login") : undefined}
                  disabled={isSubmitting || (user && isProfileComplete && !query.trim())}
                  className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-4 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Analysing AI search results…
                    </>
                  ) : !user ? (
                    <>
                      Log In to Run Report
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : !isProfileComplete ? (
                    <>
                      Complete Business Profile First
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Run OTA vs Direct Report
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                {user && <UsageMeter item={usage?.ota_queries} className="justify-center" />}
              </form>
            </div>
          </div>

          {/* Right: animated example */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Hotel className="w-4 h-4 text-outline" />
              <span className="text-xs text-on-surface-variant font-medium">
                Example: &ldquo;book Grand Palace Hotel in Paris, France&rdquo;
              </span>
            </div>
            {EXAMPLE_RESULTS.map((result) => (
              <AnimatedExampleCard key={result.provider} {...result} />
            ))}
            <p className="text-xs text-on-surface-variant/60 text-center mt-2">
              Example data — your report will show real results
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mb-24">
        <h2 className="font-headline text-3xl font-bold text-on-surface mb-12 text-center">
          How It Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              step: "1",
              icon: <Search className="w-6 h-6 text-primary" />,
              title: "AI Searches Your Hotel",
              desc: "We send your booking query to ChatGPT, Gemini, and Claude with live web search enabled.",
            },
            {
              step: "2",
              icon: <ExternalLink className="w-6 h-6 text-primary" />,
              title: "We Extract Every Link",
              desc: "Each citation URL the AI provides is extracted in order — these are the links AI is sending guests to.",
            },
            {
              step: "3",
              icon: <Shield className="w-6 h-6 text-primary" />,
              title: "Direct vs OTA Classification",
              desc: "Links to your own domain are marked Direct. Third-party platforms like Booking.com are marked OTA.",
            },
          ].map((item) => (
            <div
              key={item.step}
              className="bg-surface-container-low rounded-2xl p-8 border border-outline/10"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                {item.icon}
              </div>
              <h3 className="font-headline text-xl font-bold text-on-surface mb-2">{item.title}</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default OTAEngine;
